import React__default, { useState, useId, useEffect, useMemo, createElement, useCallback } from 'react';
import { c as classNames } from './responsive-b521ac2b.js';
import { C as Checkbox } from './errorMessages-7c43ebaa.js';

function validateAll(...inputs) {
    return inputs.reduce((prev, curr) => curr.validate() && prev, true);
}
function validate(value, validators) {
    const messages = [];
    for (const [validator, message] of validators) {
        if (!validator(value)) {
            messages.push(message);
        }
    }
    return messages;
}
function useInput(component, props) {
    var _a;
    const [value, setValue] = useState(props.value);
    const [error, setError] = useState('');
    const autoId = useId();
    const id = (_a = props.id) !== null && _a !== void 0 ? _a : autoId;
    useEffect(() => {
        setValue(props.value);
    }, [props.value]);
    const mergedProps = useMemo(() => (Object.assign(Object.assign({}, props), { id,
        value, invalid: error !== '', onChange: value => {
            var _a;
            setValue(value);
            (_a = props.onChange) === null || _a === void 0 ? void 0 : _a.call(props, value);
        } })), [props, id, value, error]);
    const input = useMemo(() => createElement(component, mergedProps), [component, mergedProps]);
    const validateValue = useCallback(() => {
        if (mergedProps.validators) {
            const messages = validate(value, mergedProps.validators);
            if (messages.length > 0) {
                setError(messages[0]);
                return false;
            }
        }
        setError('');
        return true;
    }, [mergedProps.validators, value]);
    useEffect(() => {
        if (!error && props.messages) {
            for (const getMessage of props.messages) {
                const message = getMessage(value);
                if (message) {
                    setError(message);
                    break;
                }
            }
        }
    }, [error, props.messages, value]);
    return {
        id,
        value,
        input,
        error,
        validate: validateValue,
        reset: () => {
            var _a;
            setValue(props.value);
            setError('');
            (_a = props.onChange) === null || _a === void 0 ? void 0 : _a.call(props, props.value);
        }
    };
}

function useTickBox(props) {
    return useInput(TickBox, props);
}
const TickBox = props => {
    var _a;
    function onChange(event) {
        var _a;
        (_a = props.onChange) === null || _a === void 0 ? void 0 : _a.call(props, event.target.checked);
    }
    const classes = classNames('TickBox', props.className, {
        [`TickBox-${props.variant}`]: props.variant && props.variant !== 'default'
    });
    // Ensure we have an ID if we have a label.
    const autoId = useId();
    const id = (_a = props.id) !== null && _a !== void 0 ? _a : (props.label ? autoId : undefined);
    const input = React__default.createElement(Checkbox, { id: id, name: props.name, checked: props.value, size: props.size === 'default' ? undefined : props.size, disabled: props.disabled, inputProps: {
            'aria-invalid': props.invalid,
            'aria-label': props.ariaLabel
        }, onChange: onChange });
    return React__default.createElement("div", { className: classes },
        input,
        props.label && React__default.createElement("label", { htmlFor: id }, props.label));
};

export { TickBox as T, useTickBox as a, useInput as u, validateAll as v };
